import { DocumentPreviewSkeleton } from "@/components/DocumentPreviewSkeleton";
import { FILE_VIEW_MODE, type FileViewMode } from "@/constants/fileViewMode";
import { usePdfMemoryTracking } from "@/hooks/pdfViewer";
import { pdfjsWorkerUrl } from "@/utils/pdfWorker";
import {
  DocumentLoadEvent,
  type LoadError,
  PageChangeEvent,
  PdfJs,
  type RenderError,
  RotateDirection,
  type SetRenderRange,
  SpecialZoomLevel,
  Viewer,
  type VisiblePagesRange,
  Worker,
  ZoomEvent,
} from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { rotatePlugin } from "@react-pdf-viewer/rotate";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import debounce from "lodash/debounce";
import {
  Expand,
  Maximize2,
  Minus,
  Plus,
  RotateCcw,
  RotateCw,
  Shrink,
  UnfoldHorizontal,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import "../../styles/pdfViewerZoomToolbar.css";
import { PdfViewerPageInput } from "./PdfViewerPageInput";

const PDF_STANDARD_FONTS_URL = "/pdfjs-dist/standard_fonts/";
const PDF_CMAP_URL = "/pdfjs-dist/cmaps/";

const PAGE_UI_DEBOUNCE_MS = 250;
/** Ignore spurious onPageChange while zoom/rotation layout settles (extended on each onZoom/onRotate). */
const LAYOUT_EVENT_SUPPRESS_MS = 300;
const RENDER_RANGE_BUFFER = 2;

/** Matches @react-pdf-viewer/zoom step levels. */
const ZOOM_LEVELS = [
  0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.3, 1.5, 1.7, 1.9, 2.1, 2.4, 2.7, 3.0, 3.3,
  3.7, 4.1, 4.6, 5.1, 5.7, 6.3, 7.0, 7.7, 8.5, 9.4, 10,
];

const transformGetDocumentParams = (params: PdfJs.GetDocumentParams): PdfJs.GetDocumentParams =>
  ({
    ...params,
    verbosity: 0,
    standardFontDataUrl: PDF_STANDARD_FONTS_URL,
    cMapUrl: PDF_CMAP_URL,
    cMapPacked: true,
    disableRange: false,
    disableStream: true,
    // Without this, PDF.js prefetches every chunk sequentially (~thousands of 206s on large files).
    disableAutoFetch: true,
    // Keep chunks small: merged record PDFs need many reads scattered across the whole file
    // before first render, so bytes-before-display scales with chunk size — 1MB chunks made
    // the viewer download ~the entire file up front.
    rangeChunkSize: 65536,
  }) as PdfJs.GetDocumentParams;

const setRenderRange: SetRenderRange = (range: VisiblePagesRange) => {
  if (!range?.numPages) {
    return { startPage: 0, endPage: 0 };
  }
  return {
    startPage: Math.max(0, range.startPage - RENDER_RANGE_BUFFER),
    endPage: Math.min(range.numPages - 1, range.endPage + RENDER_RANGE_BUFFER),
  };
};

function nextZoomLevel(scale: number): number {
  return ZOOM_LEVELS.find((level) => level > scale) ?? scale;
}

function prevZoomLevel(scale: number): number {
  const index = ZOOM_LEVELS.findIndex((level) => level >= scale);
  return index <= 0 ? scale : ZOOM_LEVELS[index - 1]!;
}

function normalizeScale(scale: number): number {
  return typeof scale === "number" && scale > 0 ? scale : 1;
}

export interface PdfViewerWithZoomProps {
  fileUrl: string;
  initialPage?: number;
  onPageChange?: (e: PageChangeEvent) => void;
  onDocumentLoad?: (e: DocumentLoadEvent) => void;
  fileName?: string;
  currentPage?: number;
  totalPages?: number;
  onClose?: () => void;
  showViewModeToggle?: boolean;
  viewMode?: FileViewMode;
  onViewModeChange?: (mode: FileViewMode) => void;
  ocrContent?: React.ReactNode;
  onLoadError?: (error: LoadError) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

export default function PdfViewerWithZoom(props: PdfViewerWithZoomProps) {
  return (
    <Worker workerUrl={pdfjsWorkerUrl}>
      <PdfViewerWithZoomInner key={props.fileUrl} {...props} />
    </Worker>
  );
}

function PdfViewerWithZoomInner({
  fileUrl,
  initialPage = 0,
  onPageChange,
  onDocumentLoad,
  fileName,
  currentPage: _currentPage = 0,
  totalPages = 0,
  onClose,
  showViewModeToggle = false,
  viewMode = FILE_VIEW_MODE.ORIGINAL,
  onViewModeChange,
  ocrContent,
  onLoadError,
  isFullscreen = false,
  onToggleFullscreen,
}: PdfViewerWithZoomProps) {
  const [toolbarPage, setToolbarPage] = useState(() => Math.max(0, initialPage));
  const { onDocumentLoad: trackDocumentLoad } = usePdfMemoryTracking(fileUrl);

  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;
  // PdfViewerWithZoomInner remounts on every fileUrl change (the outer PdfViewerWithZoom sets
  // key={props.fileUrl} above), so this ref naturally resets per file — no explicit reset
  // effect needed.
  const hasNotifiedLoadErrorRef = useRef(false);

  const renderError: RenderError = useCallback((error: LoadError) => {
    console.error("PDF viewer failed to load document:", error.name, error.message);
    if (!hasNotifiedLoadErrorRef.current) {
      hasNotifiedLoadErrorRef.current = true;
      // renderError can run during Viewer's render; defer so we never setState from another
      // component's render phase.
      queueMicrotask(() => onLoadErrorRef.current?.(error));
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="text-sm font-medium text-destructive-text">Failed to load document</p>
      </div>
    );
  }, []);

  // Ignores the download-percentage argument — a single consistent skeleton beats a
  // percentage-driven loader. Covers only the PDF download/parse phase while this Viewer is
  // mounted; FileSidePanel.jsx renders a separate skeleton during URL mint/re-mint, when this
  // component isn't mounted at all.
  const renderLoader = useCallback(() => <DocumentPreviewSkeleton variant="content" />, []);

  const zoomPluginInstance = zoomPlugin({ enableShortcuts: true });
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const rotatePluginInstance = rotatePlugin();
  const { CurrentScale } = zoomPluginInstance;
  const { Rotate } = rotatePluginInstance;

  const scaleRef = useRef(1);
  const loadedDocRef = useRef<PdfJs.PdfDocument | null>(null);
  const isDocumentLoadedRef = useRef(false);
  const viewerPageRef = useRef<number | null>(null);
  const anchorPageRef = useRef(Math.max(0, initialPage));
  const lastPageEventRef = useRef<PageChangeEvent | null>(null);
  const lastHandledInitialPageRef = useRef<number | null>(null);
  const suppressPageEventsUntilRef = useRef(0);
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  const initialPageRef = useRef(initialPage);
  initialPageRef.current = initialPage;

  const jumpToPageRef = useRef(pageNavigationPluginInstance.jumpToPage);
  jumpToPageRef.current = pageNavigationPluginInstance.jumpToPage;

  const notifyParentPageChangeRef = useRef(
    debounce((event: PageChangeEvent) => {
      onPageChangeRef.current?.(event);
    }, PAGE_UI_DEBOUNCE_MS)
  );

  useEffect(() => {
    return () => notifyParentPageChangeRef.current.cancel();
  }, []);

  useEffect(() => {
    viewerPageRef.current = null;
    loadedDocRef.current = null;
    isDocumentLoadedRef.current = false;
    anchorPageRef.current = Math.max(0, initialPage);
    lastHandledInitialPageRef.current = null;
    suppressPageEventsUntilRef.current = 0;
    notifyParentPageChangeRef.current.cancel();
    setToolbarPage(Math.max(0, initialPage));
  }, [fileUrl]);

  // Keep toolbar in sync when parent opens a citation (same file, new page) before/while PDF loads.
  useEffect(() => {
    const page = Math.max(0, initialPage);
    setToolbarPage(page);
    anchorPageRef.current = page;
  }, [initialPage]);

  // Programmatic jumps only when parent `initialPage` changes after load (citations).
  // Initial open uses <Viewer initialPage={...} /> — no jumpToPage on first sync.
  useEffect(() => {
    if (!isDocumentLoadedRef.current) {
      return;
    }
    if (performance.now() < suppressPageEventsUntilRef.current) {
      return;
    }

    const target = Math.max(0, initialPage);

    if (lastHandledInitialPageRef.current === null) {
      lastHandledInitialPageRef.current = target;
      setToolbarPage(target);
      if (viewerPageRef.current !== null && viewerPageRef.current !== target) {
        anchorPageRef.current = target;
        jumpToPageRef.current(target);
      }
      return;
    }

    if (target === lastHandledInitialPageRef.current) {
      return;
    }

    lastHandledInitialPageRef.current = target;

    if (viewerPageRef.current !== null && target !== viewerPageRef.current) {
      anchorPageRef.current = target;
      jumpToPageRef.current(target);
      setToolbarPage(target);
    }
  }, [initialPage, fileUrl]);

  function suppressPageEventsAfterLayoutChange() {
    const pinnedPage = viewerPageRef.current ?? toolbarPage;
    anchorPageRef.current = pinnedPage;
    const deadline = performance.now() + LAYOUT_EVENT_SUPPRESS_MS;
    suppressPageEventsUntilRef.current = Math.max(suppressPageEventsUntilRef.current, deadline);
  }

  function zoomIn() {
    zoomPluginInstance.zoomTo(nextZoomLevel(normalizeScale(scaleRef.current)));
    suppressPageEventsAfterLayoutChange();
  }

  function zoomOut() {
    zoomPluginInstance.zoomTo(prevZoomLevel(normalizeScale(scaleRef.current)));
    suppressPageEventsAfterLayoutChange();
  }

  function jumpToPage(index: number) {
    const page = Math.max(0, index);
    const doc = loadedDocRef.current;
    anchorPageRef.current = page;
    viewerPageRef.current = page;
    jumpToPageRef.current(page);
    notifyParentPageChangeRef.current.cancel();
    setToolbarPage(page);

    if (doc) {
      const event: PageChangeEvent = { currentPage: page, doc };
      lastPageEventRef.current = event;
      onPageChangeRef.current?.(event);
    }
  }

  function handleViewerPageChange(event: PageChangeEvent) {
    const isSuppressed = performance.now() < suppressPageEventsUntilRef.current;

    if (isSuppressed) {
      if (event.currentPage === anchorPageRef.current) {
        suppressPageEventsUntilRef.current = 0;
      } else {
        return;
      }
    }

    if (!isDocumentLoadedRef.current) {
      const openTarget = Math.max(0, initialPageRef.current);
      if (event.currentPage !== openTarget) {
        return;
      }
    }

    viewerPageRef.current = event.currentPage;
    anchorPageRef.current = event.currentPage;
    lastPageEventRef.current = event;

    setToolbarPage(event.currentPage);

    if (!isDocumentLoadedRef.current) {
      return;
    }

    notifyParentPageChangeRef.current(event);
  }

  function handleViewerZoom(e: ZoomEvent) {
    scaleRef.current = normalizeScale(e.scale);
    suppressPageEventsAfterLayoutChange();
  }

  function handleViewerRotate() {
    suppressPageEventsAfterLayoutChange();
  }

  function handleDocumentLoad(event: DocumentLoadEvent) {
    onDocumentLoad?.(event);
    trackDocumentLoad(event);
    loadedDocRef.current = event.doc;

    const page = Math.max(0, initialPage);
    setToolbarPage(page);
    anchorPageRef.current = page;
    if (viewerPageRef.current === null) {
      viewerPageRef.current = page;
      lastPageEventRef.current = { currentPage: page, doc: event.doc };
    }

    window.setTimeout(() => {
      isDocumentLoadedRef.current = true;
    }, 1000);
  }

  return (
    <div className="chrome-pdf-container">
      <div className="chrome-pdf-toolbar">
        <div className="chrome-pdf-toolbar-left">
          {fileName ? (
            <span className="chrome-pdf-filename" title={fileName}>
              {fileName}
            </span>
          ) : null}
          {totalPages > 0 ? (
            <>
              <div className="chrome-pdf-separator" />
              <PdfViewerPageInput
                currentPage={toolbarPage}
                totalPages={totalPages}
                onJumpToPage={jumpToPage}
              />
            </>
          ) : null}
        </div>

        <div className="chrome-pdf-toolbar-center">
          <button type="button" className="chrome-pdf-btn" onClick={zoomOut} aria-label="Zoom out">
            <Minus size={18} strokeWidth={1.5} />
          </button>
          <CurrentScale>
            {({ scale }) => {
              return <span className="chrome-pdf-zoom-level">{Math.round(scale * 100)}%</span>;
            }}
          </CurrentScale>
          <button type="button" className="chrome-pdf-btn" onClick={zoomIn} aria-label="Zoom in">
            <Plus size={18} strokeWidth={1.5} />
          </button>
          <div className="chrome-pdf-separator" />
          <Rotate direction={RotateDirection.Backward}>
            {({ onClick }) => (
              <button
                type="button"
                className="chrome-pdf-btn"
                onClick={onClick}
                aria-label="Rotate counterclockwise"
              >
                <RotateCcw size={18} strokeWidth={1.5} />
              </button>
            )}
          </Rotate>
          <Rotate direction={RotateDirection.Forward}>
            {({ onClick }) => (
              <button
                type="button"
                className="chrome-pdf-btn"
                onClick={onClick}
                aria-label="Rotate clockwise"
              >
                <RotateCw size={18} strokeWidth={1.5} />
              </button>
            )}
          </Rotate>
        </div>

        {showViewModeToggle && onViewModeChange ? (
          <div className="chrome-pdf-toolbar-view-mode">
            <select
              className="chrome-pdf-view-mode-select"
              value={viewMode}
              onChange={(e) => onViewModeChange(e.target.value as FileViewMode)}
              aria-label="View mode"
            >
              <option value={FILE_VIEW_MODE.ORIGINAL}>Original</option>
              <option value={FILE_VIEW_MODE.OCR}>OCR</option>
            </select>
          </div>
        ) : null}

        <div className="chrome-pdf-toolbar-right">
          <button
            type="button"
            className="chrome-pdf-btn"
            onClick={() => {
              zoomPluginInstance.zoomTo(SpecialZoomLevel.PageFit);
              suppressPageEventsAfterLayoutChange();
            }}
            aria-label="Fit to page"
          >
            <Maximize2 size={18} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            className="chrome-pdf-btn"
            onClick={() => {
              zoomPluginInstance.zoomTo(SpecialZoomLevel.PageWidth);
              suppressPageEventsAfterLayoutChange();
            }}
            aria-label="Fit to width"
          >
            <UnfoldHorizontal size={18} strokeWidth={1.5} />
          </button>
          {onToggleFullscreen ? (
            <button
              type="button"
              className="chrome-pdf-btn"
              onClick={onToggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Shrink size={18} strokeWidth={1.5} />
              ) : (
                <Expand size={18} strokeWidth={1.5} />
              )}
            </button>
          ) : null}
          {onClose ? (
            <>
              <div className="chrome-pdf-separator" />
              <button type="button" className="chrome-pdf-btn" onClick={onClose} aria-label="Close">
                <X size={18} strokeWidth={1.5} />
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {viewMode === FILE_VIEW_MODE.OCR ? (
          <div className="chrome-pdf-ocr-panel">{ocrContent}</div>
        ) : (
          <Viewer
            fileUrl={fileUrl}
            initialPage={initialPage}
            defaultScale={SpecialZoomLevel.PageFit}
            enableSmoothScroll={false}
            setRenderRange={setRenderRange}
            plugins={[zoomPluginInstance, pageNavigationPluginInstance, rotatePluginInstance]}
            transformGetDocumentParams={transformGetDocumentParams}
            onPageChange={handleViewerPageChange}
            onZoom={handleViewerZoom}
            onRotate={handleViewerRotate}
            onDocumentLoad={handleDocumentLoad}
            renderError={renderError}
            renderLoader={renderLoader}
          />
        )}
      </div>
    </div>
  );
}
